import type { MouseEvent, ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { TbIcon } from 'components/Figma/TbIcon';
import { cn } from 'utils/classNamesTailwind';

const selectTriggerVariants = cva(
  'select-trigger relative w-[200px] min-w-[200px] max-w-full cursor-pointer overflow-visible rounded border border-snow-500 bg-transparent p-0 text-ash-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ash-900 hover:border-ash-900 disabled:cursor-not-allowed',
  {
    variants: {
      border: {
        default: '',
        transparent: 'border-transparent hover:border-ash-900',
      },
      open: {
        false: '',
        true: 'border-ash-900',
      },
      disabled: {
        false: '',
        true: 'cursor-not-allowed border-snow-500 bg-snow-50 text-ash-300 hover:border-snow-500',
      },
      fullWidth: {
        false: '',
        true: 'w-full min-w-0 max-w-none',
      },
      size: {
        default: '',
        small: '[&_.select-trigger-content]:h-8 [&_.select-trigger-content]:px-2',
      },
    },
    compoundVariants: [
      {
        border: 'transparent',
        open: true,
        className: 'border-ash-900',
      },
    ],
  }
);

const floatingLabelVariants = cva(
  'absolute left-2 z-[1] block max-w-[calc(100%-16px)] origin-left overflow-visible text-ellipsis whitespace-nowrap bg-snow-50 px-1 py-0 text-[9px] font-light transition-[opacity,transform] duration-150 ease-out [box-sizing:content-box] [height:8px!important] [top:-6px!important] [line-height:12px!important]',
  {
    variants: {
      floated: {
        false: 'pointer-events-none translate-y-1 scale-95 opacity-0',
        true: 'translate-y-0 scale-100 opacity-100',
      },
      disabled: {
        false: '',
        true: 'bg-snow-50 text-ash-300',
      },
    },
  }
);

const triggerContentVariants = cva('flex h-[38px] min-w-0 items-center px-[10px] text-xs', {
  variants: {
    floated: {
      false: '',
      true: '',
    },
  },
});

const triggerTextVariants = cva('min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap', {
  variants: {
    hasValue: {
      false: 'text-[14px] font-thin text-ash-500',
      true: 'text-xs',
    },
    selected: {
      false: '',
      true: 'font-semibold',
    },
  },
});

export interface SelectTriggerProps {
  label: ReactNode;
  value?: ReactNode;
  selectedCount?: number;
  hasValue: boolean;
  open: boolean;
  disabled?: boolean;
  id?: string;
  onClick: (event: MouseEvent<HTMLElement>) => void;
  className?: string;
  border?: 'default' | 'transparent';
  size?: 'default' | 'small';
  fullWidth?: boolean;
  showLabel?: boolean;
  popupRole?: 'listbox' | 'tree';
}

export function SelectTrigger({
  label,
  value = '',
  selectedCount = undefined,
  hasValue,
  open,
  disabled = false,
  id = undefined,
  onClick,
  className = undefined,
  border = 'default',
  size = 'default',
  fullWidth = false,
  showLabel = true,
  popupRole = 'listbox',
}: SelectTriggerProps) {
  const shouldFloatLabel = showLabel && (open || hasValue);

  return (
    <fieldset
      id={id}
      data-testid={id ? `${id}-trigger` : 'select-trigger'}
      className={cn(selectTriggerVariants({ border, open, disabled, fullWidth, size }), className)}
      tabIndex={disabled ? -1 : 0}
      role="combobox"
      aria-label={typeof label === 'string' ? label : undefined}
      aria-haspopup={popupRole}
      aria-controls={id ? `${id}-dropdown` : undefined}
      aria-expanded={open}
      aria-disabled={disabled}
      onClick={disabled ? undefined : onClick}
      onKeyDown={event => {
        if (!disabled && (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown')) {
          event.preventDefault();
          onClick(event as unknown as React.MouseEvent<HTMLElement>);
        }
      }}
    >
      {showLabel && <legend className={floatingLabelVariants({ floated: shouldFloatLabel, disabled })}>{label}</legend>}
      <div className={cn('select-trigger-content', triggerContentVariants({ floated: shouldFloatLabel }))}>
        <span className={triggerTextVariants({ hasValue, selected: hasValue || selectedCount !== undefined })}>
          {shouldFloatLabel && selectedCount !== undefined && `(${selectedCount}) `}
          {shouldFloatLabel ? value : showLabel ? label : value}
        </span>
        <TbIcon name={open ? 'ChevronUp' : 'ChevronDown'} size="lg" className="ml-2 shrink-0" aria-hidden="true" />
      </div>
    </fieldset>
  );
}
